/**
 * 패널 관련 스타일 컴포넌트
 */
import styled from 'styled-components';

export const ResizablePanelsOuter = styled.div<{ $left?: number; $right?: number; $bottom?: number }>`
  position: fixed;
  bottom: ${props => props.$bottom ?? 20}px;
  left: ${props => props.$left ?? 20}px;
  right: ${props => props.$right ?? 20}px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  pointer-events: auto;
  max-height: calc(100vh - 92px);
`;

export const ResizablePanelsWrapper = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0;
  flex: 1;
  min-height: 0;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const ResizeHandleVertical = styled.div`
  width: 8px;
  flex-shrink: 0;
  cursor: col-resize;
  background: transparent;
  position: relative;
  touch-action: none;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 4px;
    height: 40px;
    background: ${props => props.theme.colors.gray[400]};
    border-radius: 2px;
    opacity: 0;
    transition: opacity 0.2s;
  }

  /* PC: hover 가능한 환경에서만 hover/active로 표시 */
  @media (hover: hover) and (pointer: fine) {
    &:hover::after,
    &:active::after {
      opacity: 1;
    }
  }

  /* 모바일/태블릿: hover 불가 환경에서 항상 표시 */
  @media (hover: none), (pointer: coarse) {
    width: 20px;

    &::after {
      width: 6px;
      height: 48px;
      opacity: 1;
    }
  }

  /* PC 반응형 모드 폴백: 작은 화면에서도 항상 표시 */
  @media (max-width: 1024px) {
    width: 20px;

    &::after {
      width: 6px;
      height: 48px;
      opacity: 1;
    }
  }
`;

export const ResizeHandleHorizontal = styled.div`
  height: 8px;
  flex-shrink: 0;
  cursor: row-resize;
  background: transparent;
  position: relative;
  touch-action: none;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    height: 4px;
    width: 40px;
    background: ${props => props.theme.colors.gray[400]};
    border-radius: 2px;
    opacity: 0;
    transition: opacity 0.2s;
  }

  /* PC: hover 가능한 환경에서만 hover/active로 표시 */
  @media (hover: hover) and (pointer: fine) {
    &:hover::after,
    &:active::after {
      opacity: 1;
    }
  }

  /* 모바일/태블릿: hover 불가 환경에서 항상 표시 */
  @media (hover: none), (pointer: coarse) {
    height: 20px;

    &::after {
      height: 6px;
      width: 48px;
      opacity: 1;
    }
  }

  /* PC 반응형 모드 폴백: 작은 화면에서도 항상 표시 */
  @media (max-width: 1024px) {
    height: 20px;

    &::after {
      height: 6px;
      width: 48px;
      opacity: 1;
    }
  }
`;

export const FixedLearningRecordsContainer = styled.div<{ $hasAIPanel?: boolean; $width?: number; $standalone?: boolean; $left?: number; $bottom?: number }>`
  position: ${props => props.$standalone ? 'fixed' : 'relative'};
  bottom: ${props => props.$standalone ? (props.$bottom ?? 20) : 'unset'}px;
  left: ${props => props.$standalone ? (props.$left ?? 20) : 'unset'}px;
  z-index: 100;
  pointer-events: auto;
  width: ${props => props.$width != null
    ? `${props.$width}px`
    : props.$hasAIPanel
      ? 'min(380px, calc((100vw - 60px) * 0.28))'
      : 'min(450px, calc((100vw - 40px) * 0.35))'};
  flex-shrink: ${props => props.$width != null ? 0 : 'unset'};
  min-width: 200px;
  max-height: calc(100vh - 92px);
  box-sizing: border-box;

  @media (max-width: 768px) {
    ${props => props.$width != null ? '' : `width: calc((100vw - 40px) * 0.5) !important;`}
    min-width: 200px;
  }
  @media (max-height: 500px) {
    max-height: calc(100vh - 24px);
  }
`;

export const PanelWrapper = styled.div<{ $height: number }>`
  height: ${props => props.$height}px;
  max-height: min(${props => props.$height}px, calc(100vh - 92px));
  min-height: 0;
  position: relative;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-height: 500px) {
    max-height: min(${props => props.$height}px, calc(100vh - 24px));
  }
`;

export const CollapsedButton = styled.button<{ $position: 'left' | 'right'; $elevated?: boolean }>`
  position: fixed;
  ${props => props.$position === 'left' ? 'left: 0;' : 'right: 0;'}
  bottom: 25%;
  width: 32px;
  height: 60px;
  background-color: ${props => props.theme.colors.book.color2b};
  border-radius: ${props => props.$position === 'left' ? '0 8px 8px 0' : '8px 0 0 8px'};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.10);
  transition: all 0.3s ease;
  z-index: 1001;

  &:hover {
    background-color: ${props => props.theme.colors.book.color1b};
    transform: scale(1.05);
  }

  & svg {
    width: 20px;
    height: 20px;
  }
  & svg path {
    stroke: ${props => props.theme.colors.white};
  }
  
`;

export const PageNumberIndicator = styled.button<{ $position: 'left' | 'right'; $isExpanded: boolean }>`
  position: fixed;
  ${props => props.$position === 'left' ? 'left: 0;' : 'right: 0;'}
  bottom: calc(25% - 140px);
  width: ${props => props.$isExpanded ? '200px' : '32px'};
  height: 60px;
  background-color: ${props => props.theme.colors.gray[800]};
  border-radius: ${props => props.$position === 'left' ? '0 6px 6px 0' : '6px 0 0 6px'};
  border: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${props => props.$isExpanded ? 'flex-start' : 'center'};
  gap: 8px;
  padding: ${props => props.$isExpanded ? '0 12px' : '0'};
  box-shadow: ${props => props.theme.shadows.lg};
  z-index: 1002;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;

  &:hover {
    background-color: ${props => props.theme.colors.gray[700]};
  }

  &:focus {
    outline: none;
  }
`;

export const PageNumberContent = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  flex-direction: ${props => props.$isExpanded ? 'row' : 'column'};
  align-items: center;
  justify-content: center;
  gap: ${props => props.$isExpanded ? '8px' : '4px'};
  flex: 1;
`;

export const PageNumberLabel = styled.span`
  color: ${props => props.theme.colors.white};
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
`;

export const PageNumberText = styled.span`
  color: ${props => props.theme.colors.white};
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
`;

export const PageNumberInput = styled.input`
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid ${props => props.theme.colors.gray[600]};
  border-radius: 4px;
  color: ${props => props.theme.colors.white};
  font-size: 14px;
  font-weight: 600;
  padding: 4px 8px;
  width: 60px;
  text-align: center;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: ${props => props.theme.colors.white};
    background-color: rgba(255, 255, 255, 0.15);
  }

  &::placeholder {
    color: ${props => props.theme.colors.gray[400]};
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type=number] {
    -moz-appearance: textfield;
  }
`;

export const SyncLocationButton = styled.button<{ $position: 'left' | 'right'; $isExpanded: boolean }>`
  position: fixed;
  ${props => props.$position === 'left' ? 'left: 0;' : 'right: 0;'}
  bottom: calc(25% + 68px);
  width: ${props => props.$isExpanded ? '200px' : '32px'};
  height: 60px;
  background-color: ${props => props.theme.colors.gray[800]};
  border-radius: ${props => props.$position === 'left' ? '0 6px 6px 0' : '6px 0 0 6px'};
  border: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${props => props.$isExpanded ? 'flex-start' : 'center'};
  gap: 8px;
  padding: ${props => props.$isExpanded ? '0 12px' : '0'};
  box-shadow: ${props => props.theme.shadows.lg};
  z-index: 1003;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;

  &:hover {
    background-color: ${props => props.theme.colors.gray[700]};
  }

  &:focus {
    outline: none;
  }
`;

export const SyncLocationContent = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  flex-direction: ${props => props.$isExpanded ? 'row' : 'column'};
  align-items: center;
  justify-content: center;
  gap: ${props => props.$isExpanded ? '8px' : '4px'};
  flex: 1;
`;

export const SyncLocationIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;

  svg {
    width: 18px;
    height: 18px;
    stroke: ${props => props.theme.colors.white};
    fill: none;
  }

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
    filter: brightness(0) invert(1);
  }
`;

export const SyncLocationText = styled.span`
  color: ${props => props.theme.colors.white};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  line-height: 1;
  white-space: nowrap;
`;

export const GoBackButtonWrapper = styled.div<{ $markerTop: number; $markerLeft: number }>`
  position: fixed;
  top: ${props => props.$markerTop}px;
  left: ${props => props.$markerLeft}px;
  z-index: 1003;
  display: flex;
  align-items: center;
  gap: 0;
  background: var(--bg-primary);
  border-radius: 999px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
`;

export const GoBackAction = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => props.theme.colors.black};
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.white};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.bold};
  white-space: nowrap;
  transition: background 0.15s ease;

  html.dark-mode & svg {
    filter: invert(1);
  }

`;

export const GoBackClose = styled.span`
  position: absolute;
  right: -9px;
  top: -9px;
  z-index: 9;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: none;
  border: none;
  border-left: 1px solid var(--border-color);
  cursor: pointer;
  color: var(--text-tertiary);
  transition: color 0.15s ease;
  outline: none;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const FixedAIPanelContainer = styled.div<{ $hasLearningRecords?: boolean; $width?: number; $standalone?: boolean; $right?: number; $bottom?: number }>`
  position: ${props => props.$standalone ? 'fixed' : 'relative'};
  bottom: ${props => props.$standalone ? (props.$bottom ?? 20) : 'unset'}px;
  right: ${props => props.$standalone ? (props.$right ?? 20) : 'unset'}px;
  z-index: 10000;
  pointer-events: auto;
  width: ${props => props.$width != null
    ? `${props.$width}px`
    : props.$hasLearningRecords
      ? 'min(800px, calc((100vw - 60px) * 0.55))'
      : 'min(1016px, calc((100vw - 40px) * 0.7))'};
  flex: ${props => props.$width != null && !props.$standalone ? '0 0 auto' : props.$standalone ? 'none' : 1};
  min-width: 300px;
  max-height: calc(100vh - 92px);
  box-sizing: border-box;

  @media (max-width: 768px) {
    ${props => props.$width != null ? '' : `width: calc(100vw - 40px) !important;`}
    max-width: calc(100vw - 40px);
    min-width: unset;
    left: ${props => props.$standalone ? '20px' : 'unset'};
  }

  @media (max-width: 480px) {
    ${props => props.$width != null ? '' : `width: calc(100vw - 20px) !important;`}
    max-width: calc(100vw - 20px);
    left: ${props => props.$standalone ? '10px' : 'unset'};
  }
  @media (max-height: 500px) {
    max-height: calc(100vh - 24px);
  }
`;
